import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { AccountHeader } from '@/components/account-nav'
import { Button } from '@/components/ui/button'
import { ApiError, postResetConfirm } from '@/lib/kwykedit-api'

export const Route = createFileRoute('/routes/reset')({
  component: ResetPage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : '',
  }),
})

const inputClassName =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none'

function ResetPage() {
  const navigate = useNavigate()
  const { token } = Route.useSearch()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (pending) return
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setPending(true)
    setError(null)
    void postResetConfirm(token, password)
      .then(() => navigate({ to: '/projects' }))
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not reset your password.')
      })
      .finally(() => setPending(false))
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AccountHeader />
      <div className="mx-auto max-w-md px-6 py-12">
        <h1 className="mb-2 text-2xl font-bold">Set a new password</h1>
        <p className="mb-6 text-sm text-muted-foreground">8+ characters. This signs you in.</p>
        <div className="rounded-xl border border-border bg-card p-6">
          {!token ? (
            <p className="text-sm text-destructive">
              This reset link is missing its token.{' '}
              <Link to="/forgot" className="underline hover:opacity-80">
                Request a new one
              </Link>
              .
            </p>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <label className="block text-xs text-muted-foreground">
                New password
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  className={`mt-1 ${inputClassName}`}
                />
              </label>
              <label className="block text-xs text-muted-foreground">
                Confirm new password
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  className={`mt-1 ${inputClassName}`}
                />
              </label>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={pending} className="w-full">
                {pending ? 'Saving…' : 'Set new password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
